import {
  FileText,
  ScrollText,
  FileAudio,
  Languages,
  FileUp,
  Download,
  Scissors,
  Copy,
  Save,
  Share2,
  BookOpen,
  ListChecks,
  Microscope,
  BarChart3,
  MessageCircleQuestion,
  Scale,
  FileOutput,
  Wand2,
  MessageSquare,
  RefreshCw,
  Highlighter,
  ScanLine,
  Edit,
  Webcam,
  Mic,
  Calendar,
  MonitorDot,
  Blocks,
  UserPlus,
  Headset,
  File,
  Link,
  Mic2,
  Youtube,
  ImageOff,
  Subtitles,
  Combine,
  Volume2,
  Clock,
  Settings,
  Folder,
} from 'lucide-mui';

import { FolderType } from '../../types';

// Define action types to match mediaActions
export type CommandActionType =
  | 'upload'
  | 'record-audio'
  | 'record-screen'
  | 'import-from-url'
  | 'record-video'
  | 'join-meeting'
  | 'add-team-member'
  | 'integrations'
  | 'calendar-connect'
  | 'mute-settings'
  | 'rating-settings'
  | 'recording-history'
  | 'user-settings'
  | 'team-settings'
  | 'display-settings'
  | 'ai-settings'
  | 'usage-settings'
  | 'recorder-settings'
  | 'billing-settings'
  | 'plugin-settings'
  | 'integrations-settings'
  | 'meetingbot-settings'
  | 'recovery-settings'
  | 'navigate-to-folder'
  | 'contact-support';

export interface Command {
  key: string;
  command: string;
  description: string;
  icon?: any;
  action: CommandActionType;
  variants?: string[]; // Language variants of the command
  folderId?: string; // For dynamic folder commands
}

export const commands: Command[] = [
  // Content analysis commands
  {
    key: 'summarizer',
    command: 'summarize',
    description: 'Summarize Recording',
    icon: FileText,
    action: 'import-from-url',
    variants: [
      'resumir',
      'hacer resumen',
      'sacar resumen',
      'elaborar',
      'realizar',
      'fazer',
      'dar',
    ], // Spanish and Portuguese variants
  },
  {
    key: 'content-analyze',
    command: 'analyze',
    description: 'Analyze Recording',
    icon: BarChart3,
    action: 'import-from-url',
    variants: [
      'analizar',
      'analisar',
      'análisis',
      'análise',
      'analyze recording',
      'analyze audio',
      'analyze video',
      'audio analyzer',
      'video analyzer',
      'sound analysis',
      'speech analyzer',
      'analizar grabación',
      'analisar gravação',
      'analizar audio',
      'analisar áudio',
    ],
  },
  {
    key: 'content-compare',
    command: 'compare',
    description: 'Compare Recordings',
    icon: Scale,
    action: 'import-from-url',
    variants: ['comparar', 'cotejar', 'confrontar', 'comparer'],
  },
  {
    key: 'content-identify',
    command: 'identify',
    description: 'Identify in Recording',
    icon: Microscope,
    action: 'import-from-url',
    variants: ['identificar', 'reconocer', 'reconhecer', 'determinar'],
  },
  {
    key: 'content-highlight',
    command: 'highlight',
    description: 'Highlight in Recording',
    icon: Highlighter,
    action: 'import-from-url',
    variants: ['destacar', 'resaltar', 'subrayar', 'realçar', 'evidenciar'],
  },
  {
    key: 'content-detect',
    command: 'detect',
    description: 'Detect in Recording',
    icon: ScanLine,
    action: 'import-from-url',
    variants: ['detectar', 'descubrir', 'descobrir', 'encontrar'],
  },

  // Language processing commands
  // {
  //   key: 'resumir',
  //   command: 'resumir',
  //   description: 'Resumir Grabación',
  //   icon: Globe,
  //   action: 'import-from-url'
  // }, // Removed separate resumir command as it's now a variant of summarize
  {
    key: 'content-translate',
    command: 'translate',
    description: 'Translate Recording',
    icon: Languages,
    action: 'import-from-url',
    variants: [
      'traducir',
      'traduzir',
      'interpretar',
      'transladar',
      'translate recording',
      'translate video',
      'translate audio',
      'audio translator',
      'video translator',
      'french translator',
      'hindi translator',
      'arabic translator',
      'urdu translator',
      'german translator',
      'tagalog translator',
      'traducir grabación',
      'traduzir gravação',
      'traducir video',
      'traduzir vídeo',
    ],
  },
  {
    key: 'content-transcribe',
    command: 'transcribe',
    description: 'Transcribe Recording',
    icon: FileAudio,
    action: 'import-from-url',
    variants: ['transcribir', 'transcrever', 'transcripción', 'transcrição'],
  },
  {
    key: 'content-define',
    command: 'define',
    description: 'Define terms in Recording',
    icon: BookOpen,
    action: 'import-from-url',
    variants: ['definir', 'determinar', 'explicar', 'conceptualizar'],
  },
  {
    key: 'content-explain',
    command: 'explain',
    description: 'Explain topics in Recording',
    icon: MessageCircleQuestion,
    action: 'import-from-url',
    variants: ['explicar', 'aclarar', 'esclarecer', 'detallar'],
  },

  // Note taking and content creation
  {
    key: 'notes',
    command: 'take notes',
    description: 'Take notes from Recording',
    icon: ScrollText,
    action: 'import-from-url',
    variants: [
      'tomar notas',
      'hacer apuntes',
      'anotar',
      'fazer anotações',
      'tomar apontamentos',
    ],
  },
  {
    key: 'content-list',
    command: 'list',
    description: 'Create list from Recording',
    icon: ListChecks,
    action: 'import-from-url',
    variants: ['listar', 'enumerar', 'hacer lista', 'fazer lista', 'catalogar'],
  },
  {
    key: 'content-extract',
    command: 'extract',
    description: 'Extract info from Recording',
    icon: FileOutput,
    action: 'import-from-url',
    variants: ['extraer', 'sacar', 'extrair', 'obtener', 'obter'],
  },
  {
    key: 'content-generate',
    command: 'generate',
    description: 'Generate from Recording',
    icon: Wand2,
    action: 'import-from-url',
    variants: [
      'generar',
      'hacer',
      'realizar',
      'gerar',
      'fazer',
      'criar',
      'producir',
      'produzir',
    ],
  },

  // Media management commands
  {
    key: 'media-convert',
    command: 'convert',
    description: 'Convert Recording format',
    icon: RefreshCw,
    action: 'import-from-url',
    variants: [
      'convertir',
      'transformar',
      'converter',
      'cambiar formato',
      'mudar formato',
      'convert recording',
      'change format',
      'video converter',
      'convert video',
      'convert audio',
      'convert subtitles',
      'subtitle converter',
      'convert to document',
      'video to document',
      'convert to word',
      'audio to word',
      'audio to text',
      'audio to document',
      'convertir grabación',
      'converter gravação',
    ],
  },
  {
    key: 'media-record-audio',
    command: 'record audio',
    description: 'Record audio',
    icon: Mic,
    action: 'record-audio',
    variants: [
      'grabar audio',
      'gravar áudio',
      'grabar sonido',
      'gravar som',
      'capturar audio',
    ],
  },
  {
    key: 'media-record-screen',
    command: 'record screen',
    description: 'Record screen and audio',
    icon: MonitorDot,
    action: 'record-screen',
    variants: [
      'grabar pantalla',
      'gravar tela',
      'capturar pantalla',
      'capturar ecrã',
    ],
  },
  {
    key: 'media-record-video',
    command: 'record video',
    description: 'Record video with webcam',
    icon: Webcam,
    action: 'record-video',
    variants: ['grabar video', 'gravar vídeo', 'filmar', 'capturar video'],
  },
  {
    key: 'media-record-meeting',
    command: 'record meeting',
    description: 'Record a meeting',
    icon: Headset,
    action: 'record-screen',
    variants: [
      'grabar reunión',
      'grabar reunion',
      'gravar reunião',
      'capturar reunión',
      'capturar reunião',
      'meeting recording',
      'meeting recorder',
    ],
  },
  {
    key: 'media-upload',
    command: 'upload',
    description: 'Upload media files',
    icon: FileUp,
    action: 'upload',
    variants: ['subir', 'enviar', 'cargar', 'carregar', 'importar'],
  },
  {
    key: 'media-download',
    command: 'download',
    description: 'Download Recording',
    icon: Download,
    action: 'import-from-url',
    variants: ['descargar', 'bajar', 'baixar', 'descarregar', 'obtener'],
  },
  {
    key: 'media-edit',
    command: 'edit',
    description: 'Edit Recording',
    icon: Edit,
    action: 'import-from-url',
    variants: ['editar', 'modificar', 'alterar', 'cambiar', 'mudar'],
  },
  {
    key: 'media-copy',
    command: 'copy',
    description: 'Copy to clipboard',
    icon: Copy,
    action: 'import-from-url',
    variants: ['copiar', 'duplicar', 'clonar', 'reproducir', 'reproduzir'],
  },
  {
    key: 'media-trim',
    command: 'trim',
    description: 'Trim Recording',
    icon: Scissors,
    action: 'import-from-url',
    variants: ['recortar', 'cortar', 'acortar', 'encurtar', 'podar'],
  },
  {
    key: 'media-save',
    command: 'save',
    description: 'Save to library',
    icon: Save,
    action: 'import-from-url',
    variants: ['guardar', 'salvar', 'almacenar', 'armazenar', 'conservar'],
  },
  {
    key: 'media-share',
    command: 'share',
    description: 'Share Recording',
    icon: Share2,
    action: 'import-from-url',
    variants: ['compartir', 'enviar', 'partilhar', 'distribuir', 'difundir'],
  },

  // Communication and integration commands
  {
    key: 'chat-assistant',
    command: 'chat',
    description: 'Chat with AI assistant',
    icon: MessageSquare,
    action: 'import-from-url',
    variants: ['chatear', 'conversar', 'hablar', 'falar', 'dialogar'],
  },
  {
    key: 'answer-question',
    command: 'answer',
    description: 'Get answers about Recording',
    icon: MessageCircleQuestion,
    action: 'import-from-url',
    variants: [
      'responder',
      'contestar',
      'resolver',
      'solucionar',
      'esclarecer',
    ],
  },
  {
    key: 'instant-meeting',
    command: 'meeting',
    description: 'Start an instant meeting',
    icon: MonitorDot,
    action: 'record-screen',
    variants: ['reunión', 'reunion', 'reunião', 'encuentro', 'encontro'],
  },
  {
    key: 'calendar-sync',
    command: 'sync',
    description: 'Connect to your calendar',
    icon: Calendar,
    action: 'calendar-connect',
    variants: [
      'sincronizar',
      'conectar',
      'vincular',
      'ligar',
      'associar',
      'connect calendar',
      'calendar connect',
      'setup calendar',
      'calendar setup',
      'calendar integration',
      'add calendar',
      'sync calendar',
    ],
  },
  {
    key: 'add-member',
    command: 'invite',
    description: 'Invite team members',
    icon: UserPlus,
    action: 'add-team-member',
    variants: ['invitar', 'convidar', 'añadir', 'adicionar', 'agregar'],
  },
  {
    key: 'setup-integrations',
    command: 'integrate',
    description: 'Set up integrations',
    icon: Blocks,
    action: 'integrations',
    variants: [
      'integrar',
      'usar',
      'conectar',
      'configurar',
      'enlazar',
      'ligar',
    ],
  },

  // New commands based on popular landing pages
  {
    key: 'convert-to-pdf',
    command: 'convert to pdf',
    description: 'Convert to PDF',
    icon: File,
    action: 'import-from-url',
    variants: [
      'pdf',
      'to pdf',
      'make pdf',
      'video to pdf',
      'audio to pdf',
      'mp3 to pdf',
      'voice to pdf',
      'convertir a pdf',
      'converter para pdf',
    ],
  },
  {
    key: 'link-to-mp4',
    command: 'convert link',
    description: 'Convert link to MP4',
    icon: Link,
    action: 'import-from-url',
    variants: [
      'link to video',
      'url to mp4',
      'video to url',
      'link converter',
      'html to mp4',
      'convertir enlace',
      'converter link',
      'enlace a video',
      'link para vídeo',
    ],
  },
  {
    key: 'test-microphone',
    command: 'test mic',
    description: 'Test your microphone',
    icon: Mic2,
    action: 'record-audio',
    variants: [
      'test microphone',
      'check mic',
      'voice test',
      'webcam test',
      'webcam and mic tester',
      'probar micrófono',
      'testar microfone',
      'comprobar micrófono',
      'verificar microfone',
    ],
  },
  {
    key: 'voice-rater',
    command: 'rate voice',
    description: 'Rate voice',
    icon: Mic2,
    action: 'record-audio',
    variants: [
      'voice rating',
      'voice quality',
      'voice assessment',
      'voice evaluation',
      'voice score',
      'rate microphone',
      'voice analyzer',
      'calificar voz',
      'avaliar voz',
      'evaluar voz',
    ],
  },
  {
    key: 'youtube-tools',
    command: 'youtube tools',
    description: 'Import YouTube',
    icon: Youtube,
    action: 'import-from-url',
    variants: [
      'youtube cutter',
      'youtube downloader',
      'trim youtube',
      'cut youtube',
      'download youtube',
      'save youtube',
      'youtube 4k',
      'youtube stream recorder',
      'youtube text extractor',
      'youtube ai notetaker',
      'youtube chapter generator',
      'herramientas de youtube',
      'ferramentas do youtube',
      'editar youtube',
      'procesar youtube',
    ],
  },
  {
    key: 'import-url',
    command: 'import url',
    description: 'Import from URL',
    icon: Link,
    action: 'import-from-url',
    variants: [
      'url import',
      'import from link',
      'add url',
      'process url',
      'online video',
      'web video',
      'url media',
      'import link',
      'video from url',
      'audio from url',
      'media from link',
      'importar url',
      'importar enlace',
      'importar desde url',
      'importar desde enlace',
      'importar link',
      'importar desde link',
    ],
  },
  {
    key: 'help-support',
    command: 'help',
    description: 'Get Support',
    icon: MessageCircleQuestion,
    action: 'contact-support',
    variants: [
      'support',
      'contact',
      'assistance',
      'customer support',
      'customer service',
      'contact us',
      'contact support',
      'help me',
      'need help',
      'technical support',
      'tech support',
      'report issue',
      'report problem',
      'bug report',
      'feedback',
      // Spanish variants
      'ayuda',
      'soporte',
      'contacto',
      'asistencia',
      'servicio al cliente',
      'contactar',
      'contactar soporte',
      'necesito ayuda',
      'soporte técnico',
      'reportar problema',
      'reportar error',
      'comentarios',
      // Portuguese variants
      'ajuda',
      'suporte',
      'contato',
      'assistência',
      'atendimento ao cliente',
      'contatar',
      'contatar suporte',
      'preciso de ajuda',
      'suporte técnico',
      'relatar problema',
      'relatar erro',
      'feedback',
    ],
  },
  {
    key: 'refund-support',
    command: 'refund',
    description: 'Contact Support',
    icon: MessageCircleQuestion,
    action: 'contact-support',
    variants: [
      'refund request',
      'request refund',
      'get refund',
      'refund help',
      'refund support',
      'refund assistance',
      'return money',
      'money back',
      'refund issue',
      'refund problem',
      'billing refund',
      'payment refund',
      // Spanish variants
      'reembolso',
      'solicitar reembolso',
      'obtener reembolso',
      'ayuda con reembolso',
      'soporte de reembolso',
      'devolver dinero',
      'problema de reembolso',
      'reembolso de pago',
      // Portuguese variants
      'reembolso',
      'solicitar reembolso',
      'obter reembolso',
      'ajuda com reembolso',
      'suporte de reembolso',
      'devolver dinheiro',
      'problema de reembolso',
      'reembolso de pagamento',
    ],
  },
  {
    key: 'convert-to-link',
    command: 'convert to link',
    description: 'Create shareable link',
    icon: Link,
    action: 'import-from-url',
    variants: [
      'create link',
      'make url',
      'to url',
      'video to url link',
      'convertir a enlace',
      'converter para link',
      'crear enlace',
      'criar link',
    ],
  },
  {
    key: 'remove-watermark',
    command: 'remove watermark',
    description: 'Remove watermark',
    icon: ImageOff,
    action: 'import-from-url',
    variants: [
      'delete watermark',
      'clean video',
      'watermark remover',
      'eliminar marca de agua',
      "remover marca d'água",
      'quitar marca',
      'tirar marca',
    ],
  },
  {
    key: 'add-captions',
    command: 'add captions',
    description: 'Add captions to Recording',
    icon: Subtitles,
    action: 'import-from-url',
    variants: [
      'generate subtitles',
      'create captions',
      'live caption',
      'online live caption',
      'speak subtitles',
      'añadir subtítulos',
      'adicionar legendas',
      'agregar subtítulos',
      'generar subtítulos',
      'gerar legendas',
    ],
  },
  {
    key: 'merge-media',
    command: 'merge media',
    description: 'Merge video and audio',
    icon: Combine,
    action: 'import-from-url',
    variants: [
      'combine audio video',
      'join media',
      'merge video audio',
      'combinar medios',
      'combinar mídia',
      'unir audio y video',
      'juntar áudio e vídeo',
      'fusionar',
      'fundir',
    ],
  },
  {
    key: 'dub-video',
    command: 'dub video',
    description: 'Add dubbing to Recording',
    icon: Volume2,
    action: 'import-from-url',
    variants: [
      'add voiceover',
      'voice over',
      'video dubbing',
      'doblar video',
      'dublagem de vídeo',
      'agregar voz',
      'adicionar voz',
      'narrar video',
    ],
  },
  {
    key: 'generate-minutes',
    command: 'generate minutes',
    description: 'Generate meeting minutes',
    icon: Clock,
    action: 'import-from-url',
    variants: [
      'meeting notes',
      'meeting summary',
      'meeting minutes',
      'minutes ai',
      'call summary',
      'phone call summarizer',
      'generar actas',
      'gerar atas',
      'resumen de reunión',
      'resumo de reunião',
      'notas de reunión',
      'notas de reunião',
    ],
  },
  {
    key: 'record-content',
    command: 'record content',
    description: 'New Recording',
    icon: MonitorDot,
    action: 'record-screen',
    variants: [
      'record lecture',
      'lecture recording',
      'class recording',
      'presentation recording',
      'lecture recorder',
      'ai recorder',
      'ai screen recorder',
      'mp4 recorder',
      'online screen recorder',
      'screencast online',
      'instagram recorder',
      'tiktok recorder',
      'grabar contenido',
      'gravar conteúdo',
      'grabar clase',
      'gravar aula',
      'grabar presentación',
      'gravar apresentação',
    ],
  },
  {
    key: 'record-call',
    command: 'record call',
    description: 'Record calls and meetings',
    icon: MonitorDot,
    action: 'record-screen',
    variants: [
      'call recording',
      'phone recording',
      'meeting recording',
      'meeting recorder',
      'call recorder',
      'interview recorder',
      'facetime recorder',
      'google meet recording',
      'google meet bot',
      'grabar llamada',
      'gravar chamada',
      'grabar conversación',
      'gravar conversa',
      'grabar entrevista',
      'gravar entrevista',
    ],
  },
  // Settings commands
  {
    key: 'profile-settings',
    command: 'profile settings',
    description: 'Account Settings',
    icon: Settings,
    action: 'user-settings',
    variants: [
      'user settings',
      'account settings',
      'my profile',
      'personal settings',
      'user profile',
      'change name',
      'update profile',
      'edit profile',
      'change email',
      'update personal information',
      'account management',
      'manage account',
      'my account',
      'profile page',
      'edit account',
      'configuración de perfil',
      'ajustes de usuario',
      'mi perfil',
      'ajustes personales',
      'cambiar nombre',
      'cambiar correo',
      'editar perfil',
      'mi cuenta',
    ],
  },
  {
    key: 'team-settings',
    command: 'team settings',
    description: 'Team Settings',
    icon: Settings,
    action: 'team-settings',
    variants: [
      'workspace settings',
      'organization settings',
      'manage team',
      'team management',
      'add team member',
      'remove team member',
      'update team',
      'change team name',
      'team permissions',
      'team roles',
      'manage workspace',
      'workspace management',
      'company settings',
      'edit team',
      'configuración de equipo',
      'ajustes de equipo',
      'gestionar equipo',
      'administrar equipo',
      'añadir miembro',
      'eliminar miembro',
      'cambiar nombre de equipo',
      'permisos de equipo',
      'roles de equipo',
    ],
  },
  {
    key: 'display-settings',
    command: 'display settings',
    description: 'Display Settings',
    icon: Settings,
    action: 'display-settings',
    variants: [
      'appearance settings',
      'theme settings',
      'visual settings',
      'layout settings',
      'ui settings',
      'change theme',
      'dark mode',
      'light mode',
      'change appearance',
      'customize interface',
      'change colors',
      'interface settings',
      'screen settings',
      'display mode',
      'night mode',
      'configuración de pantalla',
      'ajustes de pantalla',
      'ajustes visuales',
      'modo oscuro',
      'modo claro',
      'cambiar tema',
      'personalizar interfaz',
      'ajustes de apariencia',
      'cambiar colores',
      'modo nocturno',
    ],
  },
  {
    key: 'ai-settings',
    command: 'ai settings',
    description: 'AI Settings',
    icon: Settings,
    action: 'ai-settings',
    variants: [
      'assistant settings',
      'ai configuration',
      'intelligence settings',
      'ai preferences',
      'configure ai',
      'change ai settings',
      'update ai',
      'ai customization',
      'assistant preferences',
      'ai behavior',
      'change assistant',
      'manage ai',
      'ai options',
      'ai features',
      'ai models',
      'language settings',
      'change language',
      'notification settings',
      'email notifications',
      'turn on notifications',
      'turn off notifications',
      'notification preferences',
      'email preferences',
      'language preferences',
      'cambiar idioma',
      'idioma',
      'configuración de IA',
      'ajustes de asistente',
      'preferencias de IA',
      'configurar asistente',
      'cambiar asistente',
      'opciones de IA',
      'notificaciones por correo',
      'ajustes de idioma',
      'preferencias de notificaciones',
      'activar notificaciones',
      'desactivar notificaciones',
      'configurar notificaciones',
      'cambiar idioma a español',
      'cambiar idioma a inglés',
      'idioma español',
      'idioma inglés',
    ],
  },
  {
    key: 'usage-settings',
    command: 'usage settings',
    description: 'Usage Settings',
    icon: Settings,
    action: 'usage-settings',
    variants: [
      'statistics',
      'analytics',
      'usage stats',
      'usage metrics',
      'view usage',
      'data usage',
      'storage usage',
      'check usage',
      'view limits',
      'quota settings',
      'api usage',
      'resource usage',
      'usage limits',
      'bandwidth usage',
      'my usage',
      'usage report',
      'configuración de uso',
      'estadísticas',
      'uso de datos',
      'métricas de uso',
      'ver uso',
      'uso de almacenamiento',
      'límites de uso',
      'cuota',
      'reporte de uso',
      'mi uso',
    ],
  },
  {
    key: 'recorder-settings',
    command: 'recording settings',
    description: 'Recording Settings',
    icon: Settings,
    action: 'recorder-settings',
    variants: [
      'video settings',
      'audio settings',
      'capture settings',
      'recorder configuration',
      'recording quality',
      'recording format',
      'change recording settings',
      'video quality',
      'audio quality',
      'microphone settings',
      'camera settings',
      'video resolution',
      'recording preferences',
      'default recording settings',
      'screen recording options',
      'configuración de grabación',
      'ajustes de video',
      'ajustes de audio',
      'calidad de grabación',
      'formato de grabación',
      'calidad de video',
      'calidad de audio',
      'ajustes de micrófono',
      'ajustes de cámara',
      'resolución de video',
      'preferencias de grabación',
    ],
  },
  {
    key: 'billing-settings',
    command: 'billing settings',
    description: 'Billing Settings',
    icon: Settings,
    action: 'billing-settings',
    variants: [
      'payment settings',
      'subscription settings',
      'payment methods',
      'plan settings',
      'upgrade plan',
      'change plan',
      'cancel subscription',
      'request refund',
      'get refund',
      'refund request',
      'cancel plan',
      'cancel membership',
      'billing help',
      'payment problem',
      'payment issues',
      'update payment',
      'change payment method',
      'update credit card',
      'credit card settings',
      'manage subscription',
      'downgrade plan',
      'billing history',
      'payment history',
      'invoice settings',
      'billing address',
      'update billing',
      'change billing info',
      'pricing',
      'subscription management',
      'subscription status',
      'payment details',
      'payment confirmation',
      'billing questions',
      'payment assistance',
      'configuración de facturación',
      'ajustes de pago',
      'métodos de pago',
      'cambiar plan',
      'cancelar suscripción',
      'solicitar reembolso',
      'obtener reembolso',
      'cancelar membresía',
      'problemas de pago',
      'actualizar método de pago',
      'cambiar tarjeta',
      'administrar suscripción',
      'reducir plan',
      'historial de facturación',
      'historial de pagos',
      'dirección de facturación',
      'precios',
      'estado de suscripción',
      'detalles de pago',
      'ayuda con facturación',
    ],
  },
  {
    key: 'plugin-settings',
    command: 'api settings',
    description: 'API Settings',
    icon: Settings,
    action: 'plugin-settings',
    variants: [
      'plugin settings',
      'api configuration',
      'developer settings',
      'api keys',
      'developer options',
      'manage api keys',
      'generate api key',
      'revoke api key',
      'api tokens',
      'api access',
      'developer tools',
      'api documentation',
      'webhook settings',
      'api endpoints',
      'create api token',
      'edit api permissions',
      'api security',
      'api management',
      'developer access',
      'configuración de API',
      'ajustes de API',
      'configuración para desarrolladores',
      'claves de API',
      'opciones para desarrolladores',
      'administrar claves de API',
      'generar clave de API',
      'revocar clave de API',
      'tokens de API',
      'acceso a API',
      'herramientas para desarrolladores',
      'documentación de API',
      'seguridad de API',
    ],
  },
  {
    key: 'integrations-settings',
    command: 'integration settings',
    description: 'Integration Settings',
    icon: Settings,
    action: 'integrations-settings',
    variants: [
      'connect services',
      'third-party services',
      'app connections',
      'external services',
      'manage integrations',
      'add integration',
      'remove integration',
      'disconnect service',
      'connect apps',
      'sync settings',
      'service connections',
      'integration management',
      'authorized apps',
      'connected accounts',
      'add service connection',
      'linked accounts',
      'third-party apps',
      'external connections',
      'app settings',
      'configuración de integraciones',
      'ajustes de integración',
      'conectar servicios',
      'servicios de terceros',
      'conexiones de apps',
      'servicios externos',
      'administrar integraciones',
      'añadir integración',
      'eliminar integración',
      'desconectar servicio',
      'conectar apps',
      'ajustes de sincronización',
      'apps autorizadas',
      'cuentas conectadas',
      'cuentas vinculadas',
      'apps de terceros',
    ],
  },
  {
    key: 'meetingbot-settings',
    command: 'meeting bot settings',
    description: 'Meeting Bot Settings',
    icon: Settings,
    action: 'meetingbot-settings',
    variants: [
      'bot settings',
      'meeting assistant',
      'configure bot',
      'meeting recorder settings',
      'bot configuration',
      'meeting ai',
      'change bot settings',
      'bot preferences',
      'meeting assistant settings',
      'virtual assistant',
      'meeting automation',
      'bot behavior',
      'bot commands',
      'meeting helper',
      'configure meeting bot',
      'meeting tools',
      'automation settings',
      'configuración de bot de reuniones',
      'ajustes de bot',
      'asistente de reuniones',
      'configurar bot',
      'ajustes de grabación de reuniones',
      'configuración de bot',
      'IA de reuniones',
      'cambiar ajustes de bot',
      'preferencias de bot',
      'asistente virtual',
      'automatización de reuniones',
      'comandos de bot',
      'ayudante de reuniones',
    ],
  },
  {
    key: 'recovery-settings',
    command: 'recovery settings',
    description: 'Recovery Settings',
    icon: Settings,
    action: 'recovery-settings',
    variants: [
      'backup settings',
      'restore settings',
      'data recovery',
      'recovery options',
      'backup and restore',
      'recover data',
      'lost files',
      'retrieve files',
      'recover recordings',
      'backup data',
      'auto backup',
      'backup configuration',
      'restore backups',
      'recovery tools',
      'file recovery',
      'backup schedule',
      'recovery history',
      'emergency recovery',
      'configuración de recuperación',
      'ajustes de respaldo',
      'recuperación de datos',
      'opciones de recuperación',
      'respaldo y restauración',
      'recuperar datos',
      'archivos perdidos',
      'recuperar archivos',
      'recuperar grabaciones',
      'respaldar datos',
      'respaldo automático',
      'configuración de respaldo',
      'restaurar respaldos',
      'herramientas de recuperación',
      'recuperación de archivos',
      'programación de respaldo',
      'historial de recuperación',
      'recuperación de emergencia',
    ],
  },

  // Keep existing settings commands but update them to match the new types
  {
    key: 'mute-settings',
    command: 'mute settings',
    description: 'Mute Settings',
    icon: Settings,
    action: 'mute-settings',
    variants: [
      'auto mute',
      'microphone settings',
      'mic settings',
      'configure mic',
      'microphone config',
      'mic config',
      'audio settings',
    ],
  },
  {
    key: 'recording-history-settings',
    command: 'recording history',
    description: 'Recording History',
    icon: Settings,
    action: 'recording-history',
    variants: [
      'recording history',
      'recording config',
      'recorder settings',
      'recording options',
      'previous recordings',
      'view recordings',
      'my recordings',
    ],
  },
  {
    key: 'rating-settings',
    command: 'feedback settings',
    description: 'Feedback Settings',
    icon: Settings,
    action: 'rating-settings',
    variants: [
      'rating settings',
      'rating config',
      'feedback options',
      'configure feedback',
      'ratings',
      'reviews',
    ],
  },
];

// Function to generate dynamic folder commands
export const generateFolderCommands = (folders: FolderType[]): Command[] => {
  return folders.map((folder) => ({
    key: `folder-${folder._id}`,
    command: `@${folder.name}`,
    description: `Go to ${folder.name}`,
    icon: Folder,
    action: 'navigate-to-folder' as CommandActionType,
    folderId: folder._id, // Store the folder ID for navigation
  }));
};

export const getMatchingSuggestions = (
  query: string,
  folders: FolderType[] = []
): Command[] => {
  if (!query || query.length < 2) return [];

  const lowerQuery = query.toLowerCase();
  const maxResults = 4;

  // Generate dynamic folder commands
  const folderCommands = generateFolderCommands(folders);

  // Combine static commands with dynamic folder commands
  const allCommands = [...commands, ...folderCommands];

  // Calculate relevance score for each matching command
  const scoredCommands = allCommands
    .map((cmd) => {
      let score = 0;
      const commandLower = cmd.command.toLowerCase();
      const descriptionLower = cmd.description.toLowerCase();

      // Check command (highest priority)
      if (commandLower === lowerQuery) {
        score += 100; // Exact match
      } else if (commandLower.startsWith(lowerQuery)) {
        score += 80; // Starts with query
      } else if (commandLower.includes(lowerQuery)) {
        score += 60; // Contains query
      }

      // Check description (medium priority)
      if (descriptionLower.includes(lowerQuery)) {
        score += 40;
      }

      // Check variants (lower priority)
      if (cmd.variants) {
        const hasExactMatch = cmd.variants.some(
          (variant) => variant.toLowerCase() === lowerQuery
        );
        if (hasExactMatch) {
          score += 50; // Exact match on variant
        } else {
          const hasStartsWithMatch = cmd.variants.some((variant) =>
            variant.toLowerCase().startsWith(lowerQuery)
          );
          if (hasStartsWithMatch) {
            score += 30; // Starts with query
          } else {
            const hasContainsMatch = cmd.variants.some((variant) =>
              variant.toLowerCase().includes(lowerQuery)
            );
            if (hasContainsMatch) {
              score += 20; // Contains query
            }
          }
        }
      }

      return { cmd, score };
    })
    .filter((item) => item.score > 0) // Only keep matches
    .sort((a, b) => b.score - a.score) // Sort by score descending
    .slice(0, maxResults) // Limit to max results
    .map((item) => item.cmd); // Extract just the commands

  return scoredCommands;
};
